type BehaviorCatalogItem = {id:string;name:string;theme:"CONNECT"|"OWN"|"RESPOND"|"ELEVATE"};
export type ConstellationProjectionObservation = {meeting_id:string;leader_id:string;behavior_id:string;
  polarity:"supporting"|"opposing";confidence:"low"|"medium"|"high";source_type:string;
  evidence_excerpt:string|null;context_tags:string[];observed_at:string};
type MeetingBehaviorObservationRow=ConstellationProjectionObservation;
export function confidenceWeight(confidence:"low"|"medium"|"high"):number {
  if (confidence === "high") return 3;
  if (confidence === "medium") return 2;
  return 1;
}

export type BehaviorConstellationStateValue =
  | "dormant"
  | "emerging"
  | "strengthening"
  | "mixed"
  | "receding";

export interface BehaviorConstellationStateRow {
  leader_id: string;
  behavior_id: string;
  theme: "CONNECT" | "OWN" | "RESPOND" | "ELEVATE";
  supporting_meeting_count_30d: number;
  opposing_meeting_count_30d: number;
  support_score: number;
  opposition_score: number;
  net_intensity: number;
  state: BehaviorConstellationStateValue;
  last_supported_at: string | null;
  last_opposed_at: string | null;
  evidence_meeting_ids: string[];
  context_tags: string[];
  updated_at: string;
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function latestIso(values: string[]): string | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort();
  return sorted[sorted.length - 1] ?? null;
}

export function determineConstellationState(params: {
  supportingMeetingCount30d: number;
  opposingMeetingCount30d: number;
  supportScore: number;
  oppositionScore: number;
  hasPriorPositiveContext: boolean;
}): BehaviorConstellationStateValue {
  const {
    supportingMeetingCount30d,
    opposingMeetingCount30d,
    supportScore,
    oppositionScore,
    hasPriorPositiveContext,
  } = params;

  if (supportingMeetingCount30d === 0 && opposingMeetingCount30d === 0) {
    return "dormant";
  }

  if (
    opposingMeetingCount30d >= 2
    && oppositionScore > supportScore
    && hasPriorPositiveContext
  ) {
    return "receding";
  }

  if (supportingMeetingCount30d >= 2 && opposingMeetingCount30d >= 1) {
    return "mixed";
  }

  if (supportingMeetingCount30d >= 3 || supportScore >= 6) {
    return "strengthening";
  }

  if (supportingMeetingCount30d >= 1) {
    return "emerging";
  }

  // Opposing-only evidence without prior positive context is not dimming.
  return "dormant";
}

export function buildBehaviorConstellationStates(params: {
  leaderId: string;
  behaviors: BehaviorCatalogItem[];
  observationRows30d: MeetingBehaviorObservationRow[];
  priorSupportingCountByBehaviorId: Map<string, number>;
  nowIso?: string;
}): BehaviorConstellationStateRow[] {
  const { leaderId, behaviors, observationRows30d, priorSupportingCountByBehaviorId } = params;
  const nowIso = params.nowIso ?? new Date().toISOString();
  const byBehavior = new Map<string, MeetingBehaviorObservationRow[]>();

  for (const row of observationRows30d) {
    const rows = byBehavior.get(row.behavior_id) ?? [];
    rows.push(row);
    byBehavior.set(row.behavior_id, rows);
  }

  return behaviors.map((behavior) => {
    const rows = byBehavior.get(behavior.id) ?? [];
    const supportByMeeting = new Map<string, number>();
    const opposeByMeeting = new Map<string, number>();
    const supportDates: string[] = [];
    const opposeDates: string[] = [];
    const contextTags = new Set<string>();
    const evidenceMeetingIds = new Set<string>();

    for (const row of rows) {
      evidenceMeetingIds.add(row.meeting_id);
      for (const tag of row.context_tags || []) {
        if (tag.trim().length > 0) contextTags.add(tag.trim());
      }
      const weight = confidenceWeight(row.confidence);
      if (row.polarity === "supporting") {
        const existing = supportByMeeting.get(row.meeting_id) ?? 0;
        supportByMeeting.set(row.meeting_id, Math.max(existing, weight));
        supportDates.push(row.observed_at);
      } else {
        const existing = opposeByMeeting.get(row.meeting_id) ?? 0;
        opposeByMeeting.set(row.meeting_id, Math.max(existing, weight));
        opposeDates.push(row.observed_at);
      }
    }

    const supportingMeetingCount30d = supportByMeeting.size;
    const opposingMeetingCount30d = opposeByMeeting.size;
    const supportScore = Array.from(supportByMeeting.values()).reduce((sum, value) => sum + value, 0);
    const oppositionScore = Array.from(opposeByMeeting.values()).reduce((sum, value) => sum + value, 0);
    const denominator = supportScore + oppositionScore;
    const netIntensity = denominator > 0
      ? round2(clamp((supportScore - oppositionScore) / denominator, -1, 1))
      : 0;
    const priorSupportingCount = priorSupportingCountByBehaviorId.get(behavior.id) ?? 0;
    const hasPriorPositiveContext = priorSupportingCount >= 2 || supportingMeetingCount30d >= 2;

    const state = determineConstellationState({
      supportingMeetingCount30d,
      opposingMeetingCount30d,
      supportScore,
      oppositionScore,
      hasPriorPositiveContext,
    });

    return {
      leader_id: leaderId,
      behavior_id: behavior.id,
      theme: behavior.theme,
      supporting_meeting_count_30d: supportingMeetingCount30d,
      opposing_meeting_count_30d: opposingMeetingCount30d,
      support_score: supportScore,
      opposition_score: oppositionScore,
      net_intensity: netIntensity,
      state,
      last_supported_at: latestIso(supportDates),
      last_opposed_at: latestIso(opposeDates),
      evidence_meeting_ids: Array.from(evidenceMeetingIds),
      context_tags: Array.from(contextTags).slice(0, 16),
      updated_at: nowIso,
    };
  });
}

